/**
 * Premium pet theme — GSAP scroll reveals, SEO block after footer, reduced-motion safe.
 */
(function () {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function initScrollReveals() {
        if (!window.gsap || !window.ScrollTrigger) return;

        window.gsap.registerPlugin(window.ScrollTrigger);

        document.querySelectorAll('.section-reveal').forEach((elem) => {
            if (reducedMotion) {
                window.gsap.set(elem, { opacity: 1, y: 0 });
                return;
            }
            window.gsap.fromTo(
                elem,
                { opacity: 0, y: 36 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.75,
                    ease: 'power2.out',
                    scrollTrigger: {
                        trigger: elem,
                        start: 'top 90%',
                        once: true,
                    },
                }
            );
        });
    }

    function revealSeoSection(seo) {
        if (!seo || seo.classList.contains('is-revealed')) return;
        seo.classList.add('is-revealed');
    }

    /**
     * Show SEO stealth block after the main footer has been scrolled past (footer bottom above viewport),
     * with ScrollTrigger when GSAP is available; scroll/resize fallback always runs.
     */
    function initSeoStealthReveal() {
        const seo = document.querySelector('[data-seo-stealth]');
        const footer = document.getElementById('site-footer');
        if (!seo) return;

        if (reducedMotion) {
            revealSeoSection(seo);
            return;
        }

        function footerScrolledPast() {
            if (!footer) return true;
            return footer.getBoundingClientRect().bottom <= 8;
        }

        function seoEnteringView() {
            const r = seo.getBoundingClientRect();
            return r.top < window.innerHeight * 0.92 && r.bottom > 0;
        }

        function fallbackCheck() {
            if (seo.classList.contains('is-revealed')) return;
            if (footerScrolledPast() && seoEnteringView()) {
                revealSeoSection(seo);
            }
        }

        let stAttached = false;
        function attachScrollTrigger() {
            if (stAttached || !window.gsap || !window.ScrollTrigger || !footer) return;
            window.gsap.registerPlugin(window.ScrollTrigger);
            stAttached = true;

            /* Fires when the bottom of the main footer clears the top of the viewport (scrolled past footer). */
            window.ScrollTrigger.create({
                trigger: footer,
                start: 'bottom top',
                onEnter: () => revealSeoSection(seo),
                once: true,
            });
        }

        attachScrollTrigger();
        if (!stAttached) {
            const wait = setInterval(() => {
                attachScrollTrigger();
                if (stAttached) clearInterval(wait);
            }, 60);
            setTimeout(() => clearInterval(wait), 6000);
        }

        /* Always show SEO block once it enters the viewport (fixes short pages & IO edge cases). */
        if ('IntersectionObserver' in window) {
            const io = new IntersectionObserver(
                (entries) => {
                    entries.forEach((e) => {
                        if (e.isIntersecting) {
                            revealSeoSection(seo);
                            io.disconnect();
                        }
                    });
                },
                { rootMargin: '120px 0px 0px 0px', threshold: 0.01 }
            );
            io.observe(seo);
        }

        window.addEventListener('scroll', fallbackCheck, { passive: true });
        window.addEventListener('resize', fallbackCheck, { passive: true });
        window.addEventListener('load', fallbackCheck, { passive: true });

        requestAnimationFrame(fallbackCheck);
        setTimeout(fallbackCheck, 100);
        setTimeout(fallbackCheck, 600);

        if (window.ScrollTrigger) {
            requestAnimationFrame(() => {
                window.ScrollTrigger.refresh();
                fallbackCheck();
            });
        }
    }

    function initHeaderScrollState() {
        const threshold = 12;
        function tick() {
            document.body.classList.toggle('header-scrolled-dense', window.scrollY > threshold);
        }
        tick();
        window.addEventListener('scroll', tick, { passive: true });
    }

    function initConversionChrome() {
        const exit = document.getElementById('exit-intent-popup');
        if (exit && !reducedMotion) {
            let fired = sessionStorage.getItem('zigo_exit_intent');
            function closeExit() {
                exit.classList.remove('is-open');
                exit.setAttribute('aria-hidden', 'true');
            }
            document.addEventListener(
                'mouseout',
                function (e) {
                    if (fired || window.innerWidth < 720) return;
                    if (e.relatedTarget != null || e.toElement != null) return;
                    if (e.clientY > 48) return;
                    fired = '1';
                    sessionStorage.setItem('zigo_exit_intent', '1');
                    exit.classList.add('is-open');
                    exit.setAttribute('aria-hidden', 'false');
                },
                false
            );
            exit.querySelectorAll('[data-exit-close]').forEach(function (btn) {
                btn.addEventListener('click', closeExit);
            });
            exit.addEventListener('click', function (e) {
                if (e.target === exit) closeExit();
            });
        }

        const sticky = document.getElementById('sticky-subscribe-bar');
        if (sticky) {
            function stickyTick() {
                const path = window.location.pathname || '';
                if (path.indexOf('/cart') !== -1 || path.indexOf('/checkouts') !== -1) {
                    sticky.classList.remove('is-visible');
                    return;
                }
                if (window.innerWidth >= 900) {
                    sticky.classList.remove('is-visible');
                    return;
                }
                sticky.classList.toggle('is-visible', window.scrollY > 400);
            }
            window.addEventListener('scroll', stickyTick, { passive: true });
            window.addEventListener('resize', stickyTick, { passive: true });
            stickyTick();
        }
    }

    function boot() {
        initHeaderScrollState();
        initSeoStealthReveal();
        initConversionChrome();

        if (window.gsap && window.ScrollTrigger) {
            initScrollReveals();
        } else {
            const wait = setInterval(() => {
                if (window.gsap && window.ScrollTrigger) {
                    clearInterval(wait);
                    initScrollReveals();
                    if (window.ScrollTrigger) window.ScrollTrigger.refresh();
                }
            }, 80);
            setTimeout(() => clearInterval(wait), 5000);
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }
})();
